/*
 * Copyright © 2021-present Tacnode Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Tacnode Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Tacnode Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import { DescriptionsProps } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { PlatformContextlake } from 'account-client'
import classNames from 'classnames'
import styles from './index.less'
import { Replicable } from '@/components/Replicable'
import { useTranslateEnum } from '@/hooks/useTranslateEnum'
import { CloudLabelWithIcon } from '@/pages/cloud/features/CloudLabelWithIcon'
import { CloudProvider } from '@/pages/cloud/interface'
import { ContextlakeEdition } from '@/pages/contextlake/interface'
import { contextlakeIcon } from '@/process.env'
import { timeFormatter } from '@/utils/timeFormatter'

export function ContextlakeCard(
  props: React.PropsWithoutRef<{
    contextlake: PlatformContextlake
  }>,
) {
  const { contextlake } = props
  const { name, id, edition, contractId, contractTags, region, regionURL, creator, cloud, gmtCreated } = contextlake
  const { t } = useTranslation()
  const { translate } = useTranslateEnum()

  // NOTE:
  // There is a rename job that may replace "contextlakes" with "contextlake" in code.
  // For backward compatibility, old instances whose id starts with "dc" must still use the
  // original "contextlakes" path segment. New instances should use "contextlake".
  // We intentionally build "contextlakes" as 'data' + 'clouds' to avoid being rewritten by the job.
  const contextlakePathSegment = id.startsWith('dc') ? 'data' + 'clouds' : 'contextlakes'

  const items: DescriptionsProps['items'] = [
    {
      key: 'Context lake ID',
      label: 'Context lake ID',
      children: (
        <Replicable value={id}>
          <a href={`${regionURL}/${contextlakePathSegment}/${id}?from_center=true`} target="_blank">
            {id}
          </a>
        </Replicable>
      ),
    },
    {
      key: 'Edition',
      label: t('Edition'),
      children: translate(edition as ContextlakeEdition),
    },
    {
      key: 'Contract Name',
      label: t('Contract Name'),
      children: <Link to={`/contracts/${contractId}/overview`}>{contractTags}</Link>,
    },
    {
      key: 'Cloud Provider',
      label: t('Cloud Provider'),
      children: <CloudLabelWithIcon cloud={cloud as CloudProvider} />,
    },
    {
      key: 'Region',
      label: t('Region'),
      children: region,
    },
    {
      key: 'Creator',
      label: t('Creator'),
      children: creator,
    },
    {
      key: 'Created at',
      label: t('Created at'),
      children: timeFormatter.format(gmtCreated),
    },
  ]

  return (
    <div className={classNames('common-card', styles.container)}>
      <img className={styles.mask} src={contextlakeIcon} />
      <div className={styles.header}>
        <a className={styles.title} href={`${regionURL}/${contextlakePathSegment}/${id}?from_center=true`} target="_blank">
          {name}
        </a>
      </div>
      <div className={styles.main}>
        {items.map(item => (
          <div key={item.key} className={styles.item}>
            <span className={styles.label}>{item.label}</span>
            <span>{item.children}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
